import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle2, MailCheck, TrendingUp } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ApiError, apiPost } from '../api/client.js';
import { Button } from '../components/Button.js';
import { Field, Input } from '../components/Input.js';

const Schema = z.object({ email: z.string().email() });
type Values = z.infer<typeof Schema>;

export function LoginPage(): JSX.Element {
  const { register, handleSubmit, formState } = useForm<Values>({
    resolver: zodResolver(Schema),
  });
  const [status, setStatus] = useState<'idle' | 'sent' | 'error'>('idle');

  const onSubmit = async (v: Values): Promise<void> => {
    try {
      await apiPost('/api/auth/magic-link/request', v);
      setStatus('sent');
    } catch (err) {
      setStatus(err instanceof ApiError && err.status === 429 ? 'error' : 'sent');
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        background: 'var(--c-bg)',
        padding: 16,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 380,
          padding: 32,
          background: 'var(--c-card)',
          border: '1px solid var(--c-border)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-md)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 24,
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: 'linear-gradient(135deg, var(--c-primary), var(--c-primary-dark))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
            }}
          >
            <TrendingUp size={17} strokeWidth={2.5} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontWeight: 700, fontSize: 'var(--text-md)' }}>ebayds</span>
            <span style={{ fontSize: 10, color: 'var(--c-muted)' }}>operations hub</span>
          </div>
        </div>

        <h1 style={{ fontSize: 'var(--text-xl)', fontWeight: 600, margin: 0, marginBottom: 6 }}>
          Sign in
        </h1>
        <p
          style={{
            fontSize: 'var(--text-base)',
            color: 'var(--c-muted)',
            margin: 0,
            marginBottom: 24,
          }}
        >
          We'll email you a one-time sign-in link. No password to remember.
        </p>

        {status === 'sent' ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              padding: 14,
              borderRadius: 'var(--radius-md)',
              background: 'var(--c-success-bg)',
              color: 'var(--c-success)',
              fontSize: 'var(--text-md)',
            }}
          >
            <CheckCircle2 size={18} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>
              If your email is registered, a sign-in link was sent. Check your inbox (and
              spam folder).
            </span>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <Field label="Email" htmlFor="login-email">
              <Input
                id="login-email"
                {...register('email')}
                type="email"
                placeholder="you@example.com"
                autoFocus
              />
            </Field>
            {formState.errors.email && (
              <p
                style={{
                  color: 'var(--c-danger)',
                  fontSize: 'var(--text-sm)',
                  marginTop: -4,
                  marginBottom: 12,
                }}
              >
                {formState.errors.email.message}
              </p>
            )}
            {status === 'error' && (
              <p
                style={{
                  color: 'var(--c-danger)',
                  fontSize: 'var(--text-sm)',
                  marginBottom: 12,
                }}
              >
                Too many attempts. Try again later.
              </p>
            )}
            <Button
              type="submit"
              icon={MailCheck}
              loading={formState.isSubmitting}
              style={{ width: '100%' }}
            >
              Send sign-in link
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
